import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { initWebPushNotifications } from '../services/webPushNotifications';

const AuthContext = createContext(null);

const IMPERSONATOR_TOKEN_KEY = 'admin_auth_token';
const IMPERSONATOR_USER_KEY = 'admin_user';

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasImpersonatorToken, setHasImpersonatorToken] = useState(
        () => !!localStorage.getItem(IMPERSONATOR_TOKEN_KEY)
    );

    useEffect(() => {
        // Check if user is already logged in
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('auth_token');
        
        if (storedUser && token) {
            try {
                setUser(JSON.parse(storedUser));
                // Register web push token (non-blocking)
                initWebPushNotifications().catch(() => {});
                // Fetch fresh user data from API to ensure we have latest picture_path
                api.get('/me')
                    .then(response => {
                        if (response.data.success && response.data.data) {
                            // Preserve type from API (admin vs staff) so admin goes to pilot dashboard
                            const freshUserData = { ...response.data.data, type: response.data.data.type || 'staff' };
                            localStorage.setItem('user', JSON.stringify(freshUserData));
                            setUser(freshUserData);
                        }
                    })
                    .catch(error => {
                        console.error('Error fetching user data:', error);
                        // If API call fails, keep using stored user data
                    });
            } catch (e) {
                localStorage.removeItem('user');
                localStorage.removeItem('auth_token');
            }
        }
        setLoading(false);
    }, []);

    const login = async (portalId, password) => {
        try {
            const response = await api.post('/login', {
                portal_id: portalId,
                password: password,
            });

            if (response.data.success && response.data.data.token) {
                const token = response.data.data.token;
                const userData = response.data.data.user;
                const requiresKc = response.data.data.requires_kc_verification === true;

                localStorage.removeItem(IMPERSONATOR_TOKEN_KEY);
                localStorage.removeItem(IMPERSONATOR_USER_KEY);
                setHasImpersonatorToken(false);

                localStorage.setItem('auth_token', token);
                localStorage.setItem('user', JSON.stringify({ ...userData, type: 'staff', requires_kc_verification: requiresKc }));
                setUser({ ...userData, type: 'staff', requires_kc_verification: requiresKc });

                // Register web push token (non-blocking)
                initWebPushNotifications().catch(() => {});

                return response.data;
            }
            throw new Error(typeof response.data.message === 'string' ? response.data.message : 'Login failed');
        } catch (error) {
            const msg = error.response?.data?.message ?? error.message;
            let str = 'Login failed';
            if (typeof msg === 'string') str = msg;
            else if (msg && typeof msg === 'object' && msg.errors) str = Object.values(msg.errors).flat().join(' ');
            throw new Error(str);
        }
    };

    const startImpersonation = useCallback(async (impersonationToken) => {
        const currentToken = localStorage.getItem('auth_token');
        const currentUser = localStorage.getItem('user');
        if (!currentToken) {
            throw new Error('No active admin session to restore later.');
        }

        localStorage.setItem(IMPERSONATOR_TOKEN_KEY, currentToken);
        if (currentUser) {
            localStorage.setItem(IMPERSONATOR_USER_KEY, currentUser);
        }
        setHasImpersonatorToken(true);

        localStorage.setItem('auth_token', impersonationToken);
        const response = await api.get('/me');
        if (!response.data.success || !response.data.data) {
            throw new Error('Could not load impersonated staff profile.');
        }

        const freshUserData = {
            ...response.data.data,
            type: response.data.data.type || 'staff',
            requires_kc_verification: false,
            impersonating: true,
        };
        localStorage.setItem('user', JSON.stringify(freshUserData));
        setUser(freshUserData);
        return freshUserData;
    }, []);

    const stopImpersonation = useCallback(async () => {
        const adminToken = localStorage.getItem(IMPERSONATOR_TOKEN_KEY);
        if (!adminToken) {
            throw new Error('No admin session to restore.');
        }

        try {
            await api.post('/logout');
        } catch {
            // Still restore admin session even if staff token revoke fails.
        }

        localStorage.removeItem(IMPERSONATOR_TOKEN_KEY);
        localStorage.removeItem(IMPERSONATOR_USER_KEY);
        setHasImpersonatorToken(false);

        localStorage.setItem('auth_token', adminToken);
        const response = await api.get('/me');
        if (!response.data.success || !response.data.data) {
            throw new Error('Could not restore admin session.');
        }

        const freshUserData = {
            ...response.data.data,
            type: response.data.data.type || 'admin',
        };
        localStorage.setItem('user', JSON.stringify(freshUserData));
        setUser(freshUserData);
        return freshUserData;
    }, []);

    const logout = useCallback(async () => {
        const restoringAdmin = !!localStorage.getItem(IMPERSONATOR_TOKEN_KEY);
        if (restoringAdmin) {
            try {
                await stopImpersonation();
                return;
            } catch {
                // Fall through to full logout.
            }
        }

        try {
            await api.post('/logout');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            localStorage.removeItem(IMPERSONATOR_TOKEN_KEY);
            localStorage.removeItem(IMPERSONATOR_USER_KEY);
            setHasImpersonatorToken(false);
            setUser(null);
        }
    }, [stopImpersonation]);

    const isAuthenticated = !!localStorage.getItem('auth_token') && !!user;
    const isImpersonating = Boolean(user?.impersonating || hasImpersonatorToken);

    const value = useMemo(() => ({
        user,
        setUser,
        login,
        logout,
        startImpersonation,
        stopImpersonation,
        isAuthenticated,
        isImpersonating,
        loading,
    }), [
        user,
        login,
        logout,
        startImpersonation,
        stopImpersonation,
        isAuthenticated,
        isImpersonating,
        loading,
    ]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
